import contactGroupExampleImage from '../../assets/images/img_contact_group_example.jpg';
import Papa from 'papaparse';
import angular from 'angular';

// Import required modules at top level
const bulkEditModalTemplate = require('./contact-groups/bulk-edit-modal/bulk-edit-modal.html');
const BulkEditModalController = require('./contact-groups/bulk-edit-modal/bulk-edit-modal.controller').default;

export default class OrganizationAlertConfigController {

    /* @ngInject */
    constructor($scope, $state, $stateParams, ProjectManager, $http, ToastService, DialogService, $sce, IAMService, RulesApiService) {
        this.$scope = $scope;
        this.$state = $state;
        this.$stateParams = $stateParams;
        this.ProjectManager = ProjectManager;
        this.$http = $http;
        this.ToastService = ToastService;
        this.DialogService = DialogService;
        this.$sce = $sce;
        this.IAMService = IAMService;
        this.RulesApiService = RulesApiService;
    }

    $onInit() {
        this.stateTitle = 'Alert Configuration';
        this.orgName = this.ProjectManager.currentProject.organizationDisplayName;
        this.organizationId = this.ProjectManager.currentProject.organizationId;
        this.selectedTemplateName = this.$stateParams.selectedTemplateName || null;
        
        this.states = {
            ALERT_TEMPLATES: 'ALERT_TEMPLATES',
            ESCALATIONS: 'ESCALATIONS',
            CONTACT_GROUPS: 'CONTACT_GROUPS',
        };
        
        // Set default active state
        this.activeState = this.states.ALERT_TEMPLATES;

        // Escalation paths (loaded from API)
        this.escalationTemplates = [];
        this.loadEscalationPaths();

        // Contact Groups data
        this.contactGroups = [];
        this.loadingContactGroups = false;
        
        // Organization projects and contact group contacts needed for CSV functionality
        this.organizationProjects = [];
        this.organizationProjectsLoaded = false;
        this.loadingOrganizationProjects = false;
        this.contactGroupContacts = new Map(); // contactGroupName -> {projects: Map, loading: boolean}
        
        // Search state
        this.projectSearchTerms = new Map(); // contactGroupName -> searchTerm
        
        // Add Contact state needed for CSV functionality
        this.addingContact = null; // Format: {contactGroupName: string, projectName: string}
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
        
        // CSV Import/Export state
        this.csvImportData = null;
        this.showImportPreview = false;
        this.importErrors = [];
        this.importPreviewData = null;
        this.dragOverActive = false;
        this.importInProgress = false;
        
        // Images
        this.contactGroupExampleImage = contactGroupExampleImage;
        
        // Load contact groups and organization projects when component initializes
        this.loadContactGroups();
        this.loadOrganizationProjects();
    }

    async loadEscalationPaths() {
        try {
            const response = await this.RulesApiService.listEscalationPaths(this.organizationId, {});
            this.escalationTemplates = response.data || [];
        } catch (error) {
            console.error('Failed to load escalation paths:', error); // eslint-disable-line no-console
            this.escalationTemplates = [];
        } finally {
            this.$scope.$applyAsync();
        }
    }

    async loadOrganizationProjects() {
        if (this.organizationProjectsLoaded || this.loadingOrganizationProjects) {
            return;
        }

        this.loadingOrganizationProjects = true;
        
        // Get organization name from organizationId (format: organizations/org-name)
        const organizationName = this.organizationId.includes('/') ? 
            this.organizationId : 
            `organizations/${this.organizationId}`;
        
        try {
            const allProjects = await this.fetchAllProjectsForOrganization(organizationName);
            this.organizationProjects = allProjects || [];
            this.organizationProjectsLoaded = true;
        } catch (error) {
            this.organizationProjects = [];
        } finally {
            this.loadingOrganizationProjects = false;
            this.$scope.$applyAsync();
        }
    }

    async fetchAllProjectsForOrganization(organizationName) {
        return this.getProjectsPage(organizationName, null, []);
    }

    getProjectsPage(organizationName, pageToken, previousData) {
        const requestParams = {
            organization: organizationName,
            pageToken
        };
        return this.IAMService
            .projects(requestParams)
            .then(({ data, nextPageToken }) => {
                const allData = [...previousData, ...data];
                if (nextPageToken !== '') {
                    return this.getProjectsPage(organizationName, nextPageToken, allData);
                }
                return allData;
            });
    }

    getContactsDataForGroup(contactGroupName) {
        return this.contactGroupContacts.get(contactGroupName) || { projects: new Map(), loading: false };
    }

    async loadContactsForGroup(contactGroup) {
        const groupName = contactGroup.name;
        
        // Check if we already have data for this group
        if (this.contactGroupContacts.has(groupName)) {
            const groupData = this.contactGroupContacts.get(groupName);
            // If currently loading, wait for it to complete
            if (groupData.loading) {
                // Wait for loading to complete by polling
                // eslint-disable-next-line no-await-in-loop
                while (this.contactGroupContacts.get(groupName)?.loading) {
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
            // Already have data, just ensure UI is updated
            this.$scope.$applyAsync();
            return;
        }
        
        // Set loading state
        this.contactGroupContacts.set(groupName, {
            projects: new Map(),
            loading: true
        });
        this.$scope.$applyAsync();

        try {
            // Get organization name from organizationId (format: organizations/org-name)
            const organizationName = this.organizationId.includes('/') ? 
                this.organizationId : 
                `organizations/${this.organizationId}`;
            
            const params = {
                organization: organizationName,
                contactGroup: groupName
            };
            
            const response = await this.IAMService.listContactsForOrganization(params);
            
            // Group contacts by project
            const projectsMap = new Map();
            const contacts = response.contacts || [];
            
            contacts.forEach(contact => {
                const projectId = this.extractProjectIdFromContactName(contact.name);
                const projectName = `projects/${projectId}`;
                
                if (!projectsMap.has(projectName)) {
                    projectsMap.set(projectName, []);
                }
                projectsMap.get(projectName).push(contact);
            });

            // Update the contacts data
            this.contactGroupContacts.set(groupName, {
                projects: projectsMap,
                loading: false,
                cachedProjects: null,
                lastCacheKey: null
            });

        } catch (error) {
            this.contactGroupContacts.set(groupName, {
                projects: new Map(),
                loading: false,
                cachedProjects: null,
                lastCacheKey: null
            });
        }
        
        this.$scope.$applyAsync();
    }

    extractProjectIdFromContactName(contactName) {
        // Contact name format: "projects/{project-id}/contacts/{contact-id}"
        const parts = contactName.split('/');
        return parts[1]; // The project ID (not parts[3] which is the contact ID)
    }

    clearAllContactData() {
        this.contactGroupContacts.clear();
    }

    getProjectDisplayName(projectName) {
        // First try to find the project in the loaded organization projects
        if (this.organizationProjectsLoaded && this.organizationProjects.length > 0) {
            const project = this.organizationProjects.find(p => p.name === projectName);
            if (project && project.displayName) {
                return project.displayName.trim();
            }
        }
        
        // Fallback: extract project ID from project name and use it as display name
        const projectId = projectName.split('/')[1];
        return projectId || projectName;
    }

    setState(state) {
        this.activeState = state;
        if (state === this.states.CONTACT_GROUPS) {
            this.loadContactGroups();
        }
    }

    // Contact Groups Methods
    async loadContactGroups() {
        this.loadingContactGroups = true;
        try {
            const response = await this.IAMService.listContactGroups(this.organizationId);
            this.contactGroups = response.contactGroups || [];
        } catch (error) {
            this.contactGroups = [];
        } finally {
            this.loadingContactGroups = false;
            this.$scope.$applyAsync();
        }
    }

    async createContactGroup(displayName, description) {
        try {
            const response = await this.IAMService.createContactGroup(this.organizationId, {
                displayName: displayName,
                description: description
            });
            
            this.contactGroups.push(response);
            return response;
        } catch (error) {
            throw error;
        }
    }

    async updateContactGroup(contactGroup, displayName, description) {
        try {
            const groupId = contactGroup.name.split('/').pop();
            const response = await this.IAMService.updateContactGroup(this.organizationId, groupId, {
                displayName: displayName,
                description: description
            });
            
            // Update the contact group in the list
            const index = this.contactGroups.findIndex(cg => cg.name === contactGroup.name);
            if (index !== -1) {
                this.contactGroups[index] = response;
            }
            
            return response;
        } catch (error) {
            throw error;
        }
    }

    async deleteContactGroup(contactGroup) {
        try {
            const groupId = contactGroup.name.split('/').pop();
            await this.IAMService.deleteContactGroup(this.organizationId, groupId);
            
            // Remove from the list
            this.contactGroups = this.contactGroups.filter(cg => cg.name !== contactGroup.name);
        } catch (error) {
            throw error;
        }
    }

    getTotalContactsInGroup(contactGroupName) {
        const groupData = this.getContactsDataForGroup(contactGroupName);
        let total = 0;
        Array.from(groupData.projects.values()).forEach(contacts => {
            total += contacts.length;
        });
        return total;
    }

    // Method to get the actual contact count for display
    getDisplayContactCount(contactGroup) {
        const groupName = contactGroup.name;
        if (this.contactGroupContacts.has(groupName)) {
            const groupData = this.contactGroupContacts.get(groupName);
            // If still loading, use the original contact count to avoid showing 0
            if (groupData.loading) {
                return contactGroup.contactCount || 0;
            }
            return this.getTotalContactsInGroup(groupName);
        }
        return contactGroup.contactCount || 0;
    }

    // Contact editing methods
    startEditingContact(contact) {
        this.editingContact = {
            ...contact,
            newDisplayName: contact.displayName,
            newEmail: contact.email || '',
            newPhone: contact.phoneNumber || ''
        };
    }

    cancelEditingContact() {
        this.editingContact = null;
    }

    async saveContact() {
        if (!this.editingContact.newDisplayName.trim()) {
            this.ToastService.showSimpleText('Please enter a contact name');
            return;
        }

        // Validate phone number format (must start with + for country code)
        const hasPhone = this.editingContact.newPhone && this.editingContact.newPhone.trim();
        if (hasPhone && !this.editingContact.newPhone.trim().startsWith('+')) {
            this.ToastService.showSimpleText('Phone number must start with "+" followed by country code e.g., +15551234567');
            return;
        }

        // An edit can turn a contact into a duplicate of one of its siblings, so it is checked
        // against the same rule as adding and importing.
        const duplicateContact = this.getSiblingContacts(this.editingContact.name).find(contact => this.contactsMatch({
            displayName: this.editingContact.newDisplayName,
            email: this.editingContact.newEmail,
            phoneNumber: this.editingContact.newPhone
        }, contact));

        if (duplicateContact) {
            this.ToastService.showSimpleText(`"${duplicateContact.displayName}" already uses this email or phone number in this contact group`);
            return;
        }

        try {
            const contactData = {
                displayName: this.editingContact.newDisplayName.trim(),
                email: this.editingContact.newEmail.trim() || null,
                phoneNumber: this.editingContact.newPhone.trim() || ""
            };

            // Extract contact ID from the contact name (format: projects/{projectId}/contacts/{contactId})
            const contactId = this.editingContact.name.split('/').pop();
            const projectId = this.editingContact.name.split('/')[1];

            await this.IAMService.updateContact(projectId, contactId, contactData);

            // Update the contact in our data structure
            const updatedContact = { ...this.editingContact, ...contactData };
            this.updateContactInData(this.editingContact.name, updatedContact);

            this.ToastService.showSimpleText('Contact updated successfully');
            this.editingContact = null;
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to update contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        this.$scope.$applyAsync();
    }

    confirmDeleteContact(contact) {
        this.DialogService.confirm({
            title: 'Delete Contact',
            textContent: `Are you sure you want to delete "${contact.displayName}"? This action cannot be undone.`,
            ok: 'Delete',
            cancel: 'Cancel'
        }).then(() => {
            this.deleteContact(contact);
        });
    }

    async deleteContact(contact) {
        try {
            // Extract contact ID and project ID from the contact name
            const contactId = contact.name.split('/').pop();
            const projectId = contact.name.split('/')[1];

            await this.IAMService.deleteContact(projectId, contactId);
            
            // Clear cached projects to force refresh
            this.clearCachedProjectsForAllGroups();
            
            // Reload contact groups to get fresh data
            await this.loadContactGroups();
            
            this.ToastService.showSimpleText('Contact deleted successfully');
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to delete contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        this.$scope.$applyAsync();
    }

    // The contacts sharing a Project and Contact Group with this contact, excluding itself.
    getSiblingContacts(contactName) {
        let siblings = [];

        Array.from(this.contactGroupContacts.entries()).forEach(([, groupData]) => {
            Array.from(groupData.projects.entries()).forEach(([, contacts]) => {
                if (contacts.some(contact => contact.name === contactName)) {
                    siblings = contacts.filter(contact => contact.name !== contactName);
                }
            });
        });

        return siblings;
    }

    findContactInData(contactName) {
        const entries = Array.from(this.contactGroupContacts.entries());
        
        return entries.reduce((found, [, groupData]) => {
            if (found) return found;
            
            const projectEntries = Array.from(groupData.projects.entries());
            return projectEntries.reduce((foundInProject, [, contacts]) => {
                if (foundInProject) return foundInProject;
                return contacts.find(c => c.name === contactName) || null;
            }, null);
        }, null);
    }

    removeContactFromData(contactName) {
        Array.from(this.contactGroupContacts.entries()).forEach(([, groupData]) => {
            Array.from(groupData.projects.entries()).forEach(([, contacts]) => {
                const contactIndex = contacts.findIndex(c => c.name === contactName);
                if (contactIndex !== -1) {
                    contacts.splice(contactIndex, 1);
                }
            });
        });
    }

    updateContactInData(contactName, updatedContact) {
        // Find and update the contact in the data structure
        Array.from(this.contactGroupContacts.entries()).forEach(([, groupData]) => {
            Array.from(groupData.projects.entries()).forEach(([, contacts]) => {
                const contactIndex = contacts.findIndex(c => c.name === contactName);
                if (contactIndex !== -1) {
                    contacts[contactIndex] = { ...contacts[contactIndex], ...updatedContact };
                    // Clear cached projects to force refresh
                    groupData.cachedProjects = null;
                    groupData.lastCacheKey = null;
                }
            });
        });
    }

    clearCachedProjectsForAllGroups() {
        Array.from(this.contactGroupContacts.entries()).forEach(([, groupData]) => {
            groupData.cachedProjects = null;
            groupData.lastCacheKey = null;
        });
    }

    // Add Contact Methods
    isAddingContact(contactGroupName, projectName) {
        return this.addingContact && 
               this.addingContact.contactGroupName === contactGroupName && 
               this.addingContact.projectName === projectName;
    }

    startAddingContact(contactGroupName, project) {
        this.addingContact = {
            contactGroupName: contactGroupName,
            projectName: project.name
        };
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
    }

    cancelAddingContact() {
        this.addingContact = null;
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
    }

    async addContact() {
        if (!this.addingContact || !this.newContact.displayName.trim()) {
            this.ToastService.showSimpleText('Please enter a contact name');
            return;
        }

        // Validate that either email or phoneNumber is provided
        const hasEmail = this.newContact.email && this.newContact.email.trim();
        const hasPhone = this.newContact.phoneNumber && this.newContact.phoneNumber.trim();
        
        if (!hasEmail && !hasPhone) {
            this.ToastService.showSimpleText('Please provide either an email or phone number');
            return;
        }

        // Validate phone number format (must start with + for country code)
        if (hasPhone && !this.newContact.phoneNumber.trim().startsWith('+')) {
            this.ToastService.showSimpleText('Phone number must start with "+" followed by country code e.g., +15551234567');
            return;
        }

        // Duplicates are blocked here as well as in bulk import, since they cannot be told apart
        // afterwards and have to be cleaned up by hand.
        const groupDataForDuplicateCheck = this.getContactsDataForGroup(this.addingContact.contactGroupName);
        const existingInProject = groupDataForDuplicateCheck.projects.get(this.addingContact.projectName) || [];
        const duplicateContact = existingInProject.find(contact => this.contactsMatch({
            displayName: this.newContact.displayName,
            email: this.newContact.email,
            phoneNumber: this.newContact.phoneNumber
        }, contact));

        if (duplicateContact) {
            this.ToastService.showSimpleText(`"${duplicateContact.displayName}" already uses this email or phone number in this contact group`);
            return;
        }

        try {
            // Find the contact group
            const contactGroup = this.contactGroups.find(cg => cg.name === this.addingContact.contactGroupName);
            if (!contactGroup) {
                this.ToastService.showSimpleText('Contact group not found');
                return;
            }

            // Extract project ID from project name
            const projectId = this.addingContact.projectName.split('/')[1];

            const contactData = {
                displayName: this.newContact.displayName.trim(),
                email: this.newContact.email.trim() || null,
                phoneNumber: this.newContact.phoneNumber.trim() || null,
                contactGroup: contactGroup.name
            };

            const createdContact = await this.IAMService.createContact(projectId, contactData);
            
            // Add the new contact directly to the existing data structure (no full reload)
            const groupData = this.contactGroupContacts.get(this.addingContact.contactGroupName);
            if (groupData && groupData.projects.has(this.addingContact.projectName)) {
                // Add to existing project contacts
                groupData.projects.get(this.addingContact.projectName).push(createdContact);
            } else if (groupData) {
                // Create new project entry if it doesn't exist
                groupData.projects.set(this.addingContact.projectName, [createdContact]);
            }
            
            // Clear only the cached projects for this specific contact group to refresh counts
            if (groupData) {
                groupData.cachedProjects = null;
                groupData.lastCacheKey = null;
            }

            this.ToastService.showSimpleText('Contact added successfully');
            this.cancelAddingContact();
            
        } catch (error) {
            this.ToastService.showSimpleText(`Failed to add contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
        
        this.$scope.$applyAsync();
    }

    // Project search methods
    updateProjectSearch(contactGroupName, searchTerm) {
        this.projectSearchTerms.set(contactGroupName, searchTerm);
        // Clear cache to force re-filtering
        const groupData = this.contactGroupContacts.get(contactGroupName);
        if (groupData) {
            groupData.cachedProjects = null;
            groupData.lastCacheKey = null;
        }
    }

    getProjectSearchTerm(contactGroupName) {
        return this.projectSearchTerms.get(contactGroupName) || '';
    }

    clearProjectSearch(contactGroupName) {
        this.projectSearchTerms.delete(contactGroupName);
        // Clear cache to force re-filtering
        const groupData = this.contactGroupContacts.get(contactGroupName);
        if (groupData) {
            groupData.cachedProjects = null;
            groupData.lastCacheKey = null;
        }
    }

    highlightSearchMatch(text, searchTerm) {
        if (!searchTerm || !searchTerm.trim()) {
            return text;
        }
        
        const term = searchTerm.trim();
        let highlightedText = text;
        
        // If search term has spaces, highlight individual words
        if (term.includes(' ')) {
            const searchWords = term.split(' ').filter(word => word.trim());
            // Create a single regex that matches any of the search words
            const escapedWords = searchWords.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
            const regex = new RegExp(`(${escapedWords.join('|')})`, 'gi');
            highlightedText = highlightedText.replace(regex, '<mark style="background-color: #fff3cd; border-radius: 2px;">$1</mark>');
        } else {
            // Highlight the whole term
            const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
            highlightedText = highlightedText.replace(regex, '<mark style="background-color: #fff3cd; border-radius: 2px;">$1</mark>');
        }
        
        return this.$sce.trustAsHtml(highlightedText);
    }

    getProjectSearchPlaceholder(contactGroupName) {
        // Use total organization projects if available, otherwise fall back to projects with contacts
        let totalProjects;
        
        if (this.organizationProjectsLoaded && this.organizationProjects.length > 0) {
            totalProjects = this.organizationProjects.length;
        } else {
            // Fallback to projects with contacts if organization projects aren't loaded yet
            const groupData = this.getContactsDataForGroup(contactGroupName);
            totalProjects = groupData.projects.size;
        }
        
        if (totalProjects > 5) {
            return `Search projects (${totalProjects})`;
        }
        
        return 'Search projects...';
    }

    // CSV Export/Import Methods
    showBulkEditModal() {
        this.dragOverActive = false;
        this.csvImportData = null;
        this.importErrors = [];
        this.importPreviewData = null;
        this.importResults = null;
        
        this.DialogService.show({
            template: bulkEditModalTemplate,
            controller: BulkEditModalController,
            controllerAs: 'ctrl',
            locals: {
                parentCtrl: this
            },
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true,
            fullscreen: false
        });
    }

    async exportContactsCSV() {
        // Don't proceed if projects are still loading
        if (this.loadingOrganizationProjects) {
            return;
        }

        // Without the project list the Project column falls back to raw project IDs, and the file
        // is then rejected on re-import because those IDs match no project name. Better to fail
        // here than to hand out a file that cannot be uploaded back.
        if (!this.organizationProjectsLoaded) {
            this.ToastService.showSimpleText('Could not load the project list, so the export would not be valid for re-import. Please reload the page and try again.');
            return;
        }
        
        // Load all contacts for all contact groups before generating CSV
        await this.loadAllContactsForExport();
        
        const csvData = this.generateCSVData();
        const csvContent = this.convertToCSV(csvData);
        
        this.downloadCSV(csvContent, 'contacts-export.csv');
    }
    
    async loadAllContactsForExport() {
        // Load contacts for all contact groups to ensure we have complete data for export
        const loadPromises = this.contactGroups.map(contactGroup => {
            if (!this.contactGroupContacts.has(contactGroup.name)) {
                return this.loadContactsForGroup(contactGroup);
            }
            return Promise.resolve();
        });
        
        await Promise.all(loadPromises);
    }

    generateCSVData() {
        const csvRows = [];
        
        // Ensure we have contact groups and projects loaded
        if (!this.contactGroups || this.contactGroups.length === 0) {
            // No contact groups - return minimal template
            return [{
                Project: 'Example Project',
                'Contact Group': 'Example Contact Group',
                Name: '',
                Email: '',
                'Country Code': '',
                'Phone Number': ''
            }];
        }
        
        // Use all organization projects if available, otherwise fall back to projects with contacts
        const allProjects = new Set();
        
        if (this.organizationProjectsLoaded && this.organizationProjects.length > 0) {
            // Use all organization projects
            this.organizationProjects.forEach(project => {
                allProjects.add(project.name);
            });
        } else {
            // Fallback: collect projects that currently have contacts
            Array.from(this.contactGroupContacts.entries()).forEach(([, groupData]) => {
                Array.from(groupData.projects.keys()).forEach(projectName => {
                    allProjects.add(projectName);
                });
            });
            
            // If no projects found, add a default one
            if (allProjects.size === 0) {
                allProjects.add('projects/example-project');
            }
        }
        
        // List of valid ITU-T E.164 country codes (sorted by length descending for longest match first)
        const validCountryCodes = [
            // 4-digit codes
            '1684', '1787', '1939', '1849', '1868', '1869', '1876', '1242', '1246', '1264', '1268', '1284', '1340', '1345', '1441', '1473', '1649', '1664', '1670', '1671', '1684', '1721', '1758', '1767', '1784', '1787', '1809', '1829', '1849', '1868', '1869', '1876', '1939',
            // 3-digit codes
            '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855', '856', '880', '882', '883', '886', '888', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998',
            // 2-digit codes
            '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98',
            // 1-digit codes
            '1', '7'
        ];

        // Helper function to split phone number
        const splitPhoneNumber = (phoneNumber) => {
            if (!phoneNumber || !phoneNumber.trim()) {
                return { countryCode: '', phoneNumber: '' };
            }
            
            const phone = phoneNumber.trim();
            if (!phone.startsWith('+')) {
                return { countryCode: '', phoneNumber: phone };
            }
            
            const digits = phone.substring(1); // Remove the + sign
            if (!digits) {
                return { countryCode: '', phoneNumber: '' };
            }
            
            // Find the longest matching country code
            const matchedCountryCode = validCountryCodes.find(countryCode => digits.startsWith(countryCode));
            if (matchedCountryCode) {
                return {
                    countryCode: matchedCountryCode,
                    phoneNumber: digits.substring(matchedCountryCode.length)
                };
            }
            
            // If no valid country code found, return as-is
            return { countryCode: '', phoneNumber: digits };
        };
        
        // Add existing contacts
        this.contactGroups.forEach(contactGroup => {
            const groupName = contactGroup.name;
            const groupData = this.getContactsDataForGroup(groupName);
            
            Array.from(groupData.projects.entries()).forEach(([projectName, contacts]) => {
                contacts.forEach(contact => {
                    const { countryCode, phoneNumber } = splitPhoneNumber(contact.phoneNumber);
                    
                    csvRows.push({
                        Project: this.getProjectDisplayName(projectName),
                        'Contact Group': contactGroup.displayName,
                        Name: contact.displayName,
                        Email: contact.email || '',
                        'Country Code': countryCode,
                        'Phone Number': phoneNumber
                    });
                });
            });
        });
        
        // Add empty template rows for missing Project × Contact Group combinations
        Array.from(allProjects).forEach(projectName => {
            this.contactGroups.forEach(contactGroup => {
                const groupData = this.getContactsDataForGroup(contactGroup.name);
                
                // Check if this project already has contacts in this group
                if (!groupData.projects.has(projectName)) {
                    csvRows.push({
                        Project: this.getProjectDisplayName(projectName),
                        'Contact Group': contactGroup.displayName,
                        Name: '',
                        Email: '',
                        'Country Code': '',
                        'Phone Number': ''
                    });
                }
            });
        });
        
        // Sort by Project, then by Contact Group
        csvRows.sort((a, b) => {
            const projectCompare = a.Project.localeCompare(b.Project);
            if (projectCompare !== 0) return projectCompare;
            return a['Contact Group'].localeCompare(b['Contact Group']);
        });
        
        return csvRows;
    }

    convertToCSV(data) {
        // Always generate CSV with headers, even if no data
        if (data.length === 0) {
            // Create empty CSV with just headers
            const headers = ['Project', 'Contact Group', 'Name', 'Email', 'Country Code', 'Phone Number'];
            return Papa.unparse([{}], {
                columns: headers,
                quotes: true,
                delimiter: ',',
                header: true
            });
        }
        
        // Use Papa Parse to generate CSV with proper quoting and escaping
        const csvContent = Papa.unparse(data, {
            quotes: true, // Quote all fields
            delimiter: ',',
            header: true
        });
        
        return csvContent;
    }

    downloadCSV(content, filename) {
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    parseCSV(csvContent) {
        // Normalize line endings first - convert all to \n
        const normalizedContent = csvContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        
        // Parse CSV using Papa Parse with restricted delimiter auto-detection
        // Only allow comma, semicolon, and tab - exclude pipe
        const parseResult = Papa.parse(normalizedContent, {
            header: true,
            skipEmptyLines: true,
            trimHeaders: true,
            delimiter: '', // Auto-detect among allowed delimiters
            delimitersToGuess: [',', ';', '\t'], // Restrict auto-detection to these delimiters only
            newline: '\n', // Explicitly set line ending
            encoding: 'UTF-8',
            quoteChar: '"',
            escapeChar: '"',
            transform: (value) => {
                // Clean up any potential smart quotes or hidden characters
                return value.replace(/[""]/g, '"').replace(/['']/g, "'").trim();
            }
        });
        
        // Check for parsing errors
        if (parseResult.errors.length > 0) {
            const error = parseResult.errors[0];
            // Only log errors in development
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.error('Papa Parse error details:', error);
                // eslint-disable-next-line no-console
                console.error('CSV content around error:', csvContent.split('\n').slice(Math.max(0, (error.row || 0) - 1), (error.row || 0) + 3));
            }
            throw new Error(`CSV parsing error at line ${(error.row || 0) + 1}: ${error.message}. Check console for more details.`);
        }
        
        if (parseResult.data.length === 0) {
            throw new Error('Empty CSV file');
        }
        
        // Validate headers
        const headers = parseResult.meta.fields || [];
        const expectedHeaders = ['Project', 'Contact Group', 'Name', 'Email', 'Country Code', 'Phone Number'];
        
        const missingHeaders = expectedHeaders.filter(expectedHeader => !headers.includes(expectedHeader));
        if (missingHeaders.length > 0) {
            throw new Error(`Missing required column: ${missingHeaders[0]}. Found columns: ${headers.join(', ')}`);
        }
        
        // Add line numbers for error reporting and clean up data
        const data = parseResult.data.map((row, index) => {
            // Trim all values and add line number
            const cleanRow = {};
            Object.keys(row).forEach(key => {
                cleanRow[key] = (row[key] || '').trim();
            });
            cleanRow.lineNumber = index + 2; // +2 because Papa Parse is 0-indexed and we skip header
            return cleanRow;
        });
        
        return data;
    }

    // Two contacts match on an exact, normalized email or phone; blank values never match.
    contactsMatch(a, b) {
        const emailA = a && a.email ? a.email.trim().toLowerCase() : '';
        const emailB = b && b.email ? b.email.trim().toLowerCase() : '';
        if (emailA && emailB && emailA === emailB) {
            return true;
        }

        const phoneA = a && a.phoneNumber ? a.phoneNumber.replace(/\D/g, '') : '';
        const phoneB = b && b.phoneNumber ? b.phoneNumber.replace(/\D/g, '') : '';
        if (phoneA && phoneB && phoneA === phoneB) {
            return true;
        }

        // A contact with neither an email nor a phone has no identity to match on, so fall back
        // to the name. Without this, a Name-only row exported from Studio cannot be re-imported:
        // it would look like a new contact and fail the email-or-phone check, blocking the whole
        // file. Only applies when both sides are identity-less, so contacts that do have an email
        // or phone are never merged on a shared name.
        if (!emailA && !phoneA && !emailB && !phoneB) {
            const nameA = a && a.displayName ? a.displayName.trim() : '';
            const nameB = b && b.displayName ? b.displayName.trim() : '';
            return Boolean(nameA && nameA === nameB);
        }

        return false;
    }

    validateCSVData(data) {
        const errors = [];
        const validData = [];

        // Project names in the file are matched against the organization's projects, so a
        // renamed or mistyped project is reported here instead of failing per contact during
        // import. Left null when the project list is unavailable, since blocking the upload
        // on data we could not load would be worse; the import guards that case.
        const knownProjectNames = this.organizationProjectsLoaded && this.organizationProjects.length > 0
            ? new Set(this.organizationProjects.map(project => (project.displayName || '').trim()))
            : null;

        // Get current contacts organized by project and contact group for validation
        const currentContacts = new Map(); // key: "projectDisplayName|contactGroup", value: contact[]

        this.contactGroups.forEach(contactGroup => {
            const groupData = this.getContactsDataForGroup(contactGroup.name);
            Array.from(groupData.projects.entries()).forEach(([projectName, contacts]) => {
                const projectDisplayName = this.getProjectDisplayName(projectName);
                contacts.forEach(contact => {
                    const key = `${projectDisplayName}###${contactGroup.displayName}`;
                    if (!currentContacts.has(key)) {
                        currentContacts.set(key, []);
                    }
                    currentContacts.get(key).push(contact);
                });
            });
        });

        // Emails/phones already used earlier in this file, per Project + Contact Group.
        const seenInFile = new Map();

        data.forEach(row => {
            const rowErrors = [];

            // Skip empty rows (all fields empty)
            if (!row.Name && !row.Email && !row['Country Code'] && !row['Phone Number']) {
                return; // Skip to next iteration
            }

            // Validate required fields
            if (!row.Name || !row.Name.trim()) {
                rowErrors.push('Name is required');
            }

            if (!row.Project || !row.Project.trim()) {
                rowErrors.push('Project is required');
            }

            if (!row['Contact Group'] || !row['Contact Group'].trim()) {
                rowErrors.push('Contact Group is required');
            }

            if (knownProjectNames && row.Project && row.Project.trim() && !knownProjectNames.has(row.Project.trim())) {
                rowErrors.push(`Project "${row.Project.trim()}" was not found. Check that it matches a Project name in Studio exactly, as it may have been renamed since this file was created.`);
            }

            // Validate email format
            if (row.Email && row.Email.trim()) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(row.Email.trim())) {
                    rowErrors.push('Invalid email format');
                }
            }

            // Validate phone number format
            if (row['Country Code'] && row['Country Code'].trim() && row['Phone Number'] && row['Phone Number'].trim()) {
                const countryCode = row['Country Code'].trim();
                const phoneNumber = row['Phone Number'].trim();

                // Clean the country code by removing all non-digit characters
                const cleanedCountryCodeDigits = countryCode.replace(/\D/g, '');

                // Clean the phone number by removing all non-digit characters
                const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');

                // Validate that we have digits in the country code
                if (cleanedCountryCodeDigits.length === 0) {
                    rowErrors.push('Country code must contain digits e.g., 1 or 47');
                }

                // Validate that we have digits in the phone number
                if (cleanedPhoneNumber.length === 0) {
                    rowErrors.push('Phone number must contain digits e.g., 5551234567');
                }

                // Store the cleaned combined version with + prefix for later use
                if (cleanedCountryCodeDigits.length > 0 && cleanedPhoneNumber.length > 0) {
                    row.cleanedPhone = `+${cleanedCountryCodeDigits}${cleanedPhoneNumber}`;
                }
            } else if (row['Country Code'] && row['Country Code'].trim() && (!row['Phone Number'] || !row['Phone Number'].trim())) {
                // Country code provided but no phone number
                rowErrors.push('Phone number is required when country code is provided');
            } else if ((!row['Country Code'] || !row['Country Code'].trim()) && row['Phone Number'] && row['Phone Number'].trim()) {
                // Phone number provided but no country code
                rowErrors.push('Country code is required when phone number is provided');
            }

            const rowIdentity = { displayName: row.Name, email: row.Email, phoneNumber: row.cleanedPhone };

            const key = `${row.Project}###${row['Contact Group']}`;
            const existingContacts = currentContacts.get(key) || [];
            const isNewContact = !existingContacts.find(c => this.contactsMatch(rowIdentity, c));

            // For new contacts, require either email or phoneNumber
            if (isNewContact) {
                const hasEmail = row.Email && row.Email.trim();
                const hasPhone = row['Country Code'] && row['Country Code'].trim() && row['Phone Number'] && row['Phone Number'].trim();

                if (!hasEmail && !hasPhone) {
                    rowErrors.push('New contacts must have either an email or phone number');
                }
            }

            if (rowIdentity.email || rowIdentity.phoneNumber) {
                if (!seenInFile.has(key)) {
                    seenInFile.set(key, []);
                }
                const rowsSeenForKey = seenInFile.get(key);
                const duplicateRow = rowsSeenForKey.find(seen => this.contactsMatch(rowIdentity, seen));

                if (duplicateRow) {
                    rowErrors.push(`Duplicate contact: this email/phone already appears on line ${duplicateRow.lineNumber} for this Project and Contact Group`);
                }

                rowsSeenForKey.push({ email: rowIdentity.email, phoneNumber: rowIdentity.phoneNumber, lineNumber: row.lineNumber });
            }

            if (rowErrors.length > 0) {
                errors.push({
                    line: row.lineNumber,
                    data: row,
                    errors: rowErrors
                });
            } else {
                validData.push(row);
            }
        });

        return { errors, validData };
    }

    generateImportPreview() {
        // Safety check: ensure csvImportData exists and is an array
        if (!this.csvImportData || !Array.isArray(this.csvImportData)) {
            this.importPreviewData = null;
            return;
        }

        const preview = {
            toAdd: [],
            toUpdate: [],
            toRemove: [],
            byProject: {}
        };
        
        // Get current contacts organized by project and contact group
        const currentContacts = new Map(); // key: "projectDisplayName|contactGroup", value: contact
        
        this.contactGroups.forEach(contactGroup => {
            const groupData = this.getContactsDataForGroup(contactGroup.name);
            Array.from(groupData.projects.entries()).forEach(([projectName, contacts]) => {
                const projectDisplayName = this.getProjectDisplayName(projectName);
                contacts.forEach(contact => {
                    const key = `${projectDisplayName}###${contactGroup.displayName}`;
                    if (!currentContacts.has(key)) {
                        currentContacts.set(key, []);
                    }
                    currentContacts.get(key).push(contact);
                });
            });
        });
        
        // Process import data
        const importContacts = new Map(); // key: "projectDisplayName|contactGroup", value: contacts[]
        
        this.csvImportData.forEach(row => {
            const key = `${row.Project}###${row['Contact Group']}`;
            if (!importContacts.has(key)) {
                importContacts.set(key, []);
            }
            importContacts.get(key).push({
                displayName: row.Name.trim(),
                email: row.Email.trim() || null,
                phoneNumber: row.cleanedPhone || (row['Country Code'] && row['Phone Number'] ? 
                    (`+${row['Country Code'].trim().replace(/\D/g, '')}${row['Phone Number'].trim().replace(/\D/g, '')}`) : null),
                csvRow: row,
                originalPhone: (row['Country Code'] && row['Phone Number'] ? (`${row['Country Code'].trim()} ${row['Phone Number'].trim()}`) : null) // Keep original for showing transformation
            });
        });
        
        // Each existing contact is consumed at most once, so pre-existing duplicates aren't
        // all matched to the same imported row and left un-removable.
        const allKeys = new Set([...currentContacts.keys(), ...importContacts.keys()]);

        Array.from(allKeys).forEach(key => {
            const newContactsForKey = importContacts.get(key) || [];
            const unmatchedCurrentContacts = [...(currentContacts.get(key) || [])];

            newContactsForKey.forEach(newContact => {
                const matchIndex = unmatchedCurrentContacts.findIndex(c => this.contactsMatch(newContact, c));

                if (matchIndex === -1) {
                    preview.toAdd.push({
                        key,
                        contact: newContact
                    });
                    return;
                }

                const [existingContact] = unmatchedCurrentContacts.splice(matchIndex, 1);

                // Check if contact details have changed (normalize empty values to null for comparison)
                const normalizeValue = (value) => value || null;

                const existingEmail = normalizeValue(existingContact.email);
                const existingPhone = normalizeValue(existingContact.phoneNumber);
                const newEmail = normalizeValue(newContact.email);
                const newPhone = normalizeValue(newContact.phoneNumber);

                if (existingContact.displayName !== newContact.displayName || existingEmail !== newEmail || existingPhone !== newPhone) {
                    // For updates, also store the original phone for display purposes
                    const updatedContact = { ...newContact };
                    if (newContact.originalPhone && newContact.originalPhone !== newContact.phoneNumber) {
                        updatedContact.originalPhone = newContact.originalPhone;
                    }

                    preview.toUpdate.push({
                        key,
                        old: existingContact,
                        new: updatedContact
                    });
                }
            });

            unmatchedCurrentContacts.forEach(currentContact => {
                preview.toRemove.push({
                    key,
                    contact: currentContact
                });
            });
        });
        
        // Group by project for the new UI
        const projectGroups = {};
        
        // Helper function to add item to project group
        const addToProjectGroup = (item, type) => {
            const projectName = item.key.split('###')[0];
            if (!projectGroups[projectName]) {
                projectGroups[projectName] = {
                    toAdd: [],
                    toUpdate: [],
                    toRemove: []
                };
            }
            projectGroups[projectName][type].push(item);
        };
        
        // Group all items by project
        preview.toAdd.forEach(item => addToProjectGroup(item, 'toAdd'));
        preview.toUpdate.forEach(item => addToProjectGroup(item, 'toUpdate'));
        preview.toRemove.forEach(item => addToProjectGroup(item, 'toRemove'));
        
        preview.byProject = projectGroups;
        this.importPreviewData = preview;
    }

    async applyCSVImport(showToast = true) {
        if (!this.importPreviewData) {
            return;
        }

        this.importInProgress = true;
        this.$scope.$applyAsync();

        try {
            const results = {
                added: 0,
                updated: 0,
                removed: 0,
                errors: []
            };

            // Process additions in parallel
            const addPromises = this.importPreviewData.toAdd.map(async item => {
                try {
                    const [projectDisplayName, contactGroupDisplayName] = item.key.split('###');
                    const projectId = this.getProjectIdFromDisplayName(projectDisplayName);
                    if (!projectId) {
                        results.errors.push(`Project "${projectDisplayName}" was not found. It may have been renamed in Studio since this file was created.`);
                        return;
                    }

                    const contactGroup = this.contactGroups.find(cg => cg.displayName === contactGroupDisplayName);
                    if (!contactGroup) {
                        results.errors.push(`Contact group "${contactGroupDisplayName}" not found`);
                        return;
                    }

                    const contactData = {
                        displayName: item.contact.displayName,
                        email: item.contact.email,
                        phoneNumber: item.contact.phoneNumber,
                        contactGroup: contactGroup.name
                    };

                    await this.IAMService.createContact(projectId, contactData);
                    results.added += 1;
                } catch (error) {
                    results.errors.push(`Failed to add contact "${item.contact.displayName}": ${error.data?.error || error.message || 'Unknown error'}`);
                }
            });

            await Promise.all(addPromises);

            // Process updates in parallel
            const updatePromises = this.importPreviewData.toUpdate.map(async item => {
                try {
                    const contactData = {
                        displayName: item.new.displayName,
                        email: item.new.email,
                        phoneNumber: item.new.phoneNumber
                    };

                    // Extract contact ID from the contact name (format: projects/{projectId}/contacts/{contactId})
                    const contactId = item.old.name.split('/').pop();
                    const projectId = item.old.name.split('/')[1];

                    await this.IAMService.updateContact(projectId, contactId, contactData);
                    results.updated += 1;
                } catch (error) {
                    results.errors.push(`Failed to update contact "${item.new.displayName}": ${error.data?.error || error.message || 'Unknown error'}`);
                }
            });

            await Promise.all(updatePromises);

            // Process removals in parallel
            const removePromises = this.importPreviewData.toRemove.map(async item => {
                try {
                    // Extract contact ID from the contact name (format: projects/{projectId}/contacts/{contactId})
                    const contactId = item.contact.name.split('/').pop();
                    const projectId = item.contact.name.split('/')[1];

                    await this.IAMService.deleteContact(projectId, contactId);
                    results.removed += 1;
                } catch (error) {
                    results.errors.push(`Failed to remove contact "${item.contact.displayName}": ${error.data?.error || error.message || 'Unknown error'}`);
                }
            });

            await Promise.all(removePromises);

            // Clear all contact data to force fresh reload from API
            this.clearAllContactData();
            
            // Reload contact groups to get fresh data
            await this.loadContactGroups();

            // Store results for display in modal
            this.importResults = results;
            
            // Show results
            let message = '';
            if (results.added > 0) message += `Added ${results.added} contact${results.added > 1 ? 's' : ''}. `;
            if (results.updated > 0) message += `Updated ${results.updated} contact${results.updated > 1 ? 's' : ''}. `;
            if (results.removed > 0) message += `Removed ${results.removed} contact${results.removed > 1 ? 's' : ''}. `;
            
            if (results.errors.length > 0) {
                message += `${results.errors.length} error${results.errors.length > 1 ? 's' : ''} occurred.`;
                
                if (showToast) {
                    this.ToastService.showSimpleText(message);
                }
                
                // Show detailed errors in console for debugging
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console
                    console.error('Import errors:', results.errors);
                    // eslint-disable-next-line no-console
                    results.errors.forEach(error => console.error('Import error:', error));
                }
            } else if (showToast) {
                this.ToastService.showSimpleText(message || 'Import completed successfully');
            }

        } catch (error) {
            this.importResults = {
                added: 0,
                updated: 0,
                removed: 0,
                errors: [`Import failed: ${error.data?.error || error.message || 'Unknown error'}`]
            };
            if (showToast) {
                this.ToastService.showSimpleText('Error applying import. Please try again.');
            }
        } finally {
            this.importInProgress = false;
            this.$scope.$applyAsync();
        }
    }

    // Returns null when the name matches no project. Callers must handle that rather than
    // passing it to the API: a slugified display name is never a valid project ID.
    getProjectIdFromDisplayName(displayName) {
        if (this.organizationProjectsLoaded && this.organizationProjects.length > 0) {
            const project = this.organizationProjects.find(p => (p.displayName || '').trim() === displayName);
            if (project && project.name) {
                // Extract project ID from project name: "projects/{PROJECT_ID}"
                return project.name.split('/')[1];
            }
        }

        return null;
    }

    handleCSVFile(file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csvContent = e.target.result;
                const parsedData = this.parseCSV(csvContent);
                const validationResult = this.validateCSVData(parsedData);
                
                this.importErrors = validationResult.errors;
                
                if (validationResult.errors.length === 0) {
                    this.csvImportData = validationResult.validData;
                    this.generateImportPreview();
                }
                
            } catch (error) {
                this.importErrors = [{
                    line: 1,
                    errors: [error.message]
                }];
            }
            
            this.$scope.$applyAsync();
        };
        
        reader.onerror = (e) => {
            if (process.env.NODE_ENV === 'development') {
                // eslint-disable-next-line no-console
                console.error('FileReader error:', e);
            }

            this.importErrors = [{
                line: 1,
                errors: ['Could not read the selected file. Please try again or choose a different file.']
            }];
            this.$scope.$applyAsync();
        };
        
        reader.readAsText(file);
    }

    showContactGroupsInfo() {
        this.DialogService.show({
            template: `
                <md-dialog class="contact-groups-info-dialog" style="max-width: 600px; width: 100%;">
                    <md-toolbar style="background-color: #f8f9fa; color: #15394c;">
                        <div class="md-toolbar-tools">
                            <div style="font-size: 16px; font-weight: 600;">What are Contact Groups?</div>
                            <span flex></span>
                            <md-button class="md-icon-button" ng-click="closeDialog()">
                                <md-icon class="text-color-icon" md-svg-icon="close"></md-icon>
                            </md-button>
                        </div>
                    </md-toolbar>
                    
                    <md-dialog-content style="padding: 24px;">
                        <p style="margin-bottom: 16px; font-size: 14px; line-height: 1.5;">
                            Contact groups are collections of people who should receive specific types of alerts. They help you organize your notification recipients and make it easy to send alerts to the right people at the right time.
                        </p>
                        
                        <div style="margin-bottom: 16px; text-align: center;">
                            <img ng-src="{{contactGroupExampleImage}}" 
                                 alt="Contact Group Example" 
                                 style="max-width: 80%; height: auto; border-radius: 8px; border: 1px solid #4a727f2e;">
                        </div>
                        
                        <div style="margin-bottom: 16px;">
                            <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">Examples of contact groups:</h4>
                            <ul style="margin: 0; padding-left: 20px; font-size: 14px; line-height: 1.6;">
                                <li style="margin-bottom: 4px;"><strong>Store Employees</strong> - Front-line staff who need operational alerts</li>
                                <li style="margin-bottom: 4px;"><strong>District Managers</strong> - Regional management for escalated issues</li>
                                <li style="margin-bottom: 4px;"><strong>Maintenance Team</strong> - Technical staff for equipment-related alerts</li>
                                <li style="margin-bottom: 4px;"><strong>Security Personnel</strong> - Staff responsible for security incidents</li>
                            </ul>
                        </div>
                        
                        <p style="margin: 0; font-size: 14px; line-height: 1.5;">
                            By organizing contacts into groups, you can quickly assign different alert templates to send notifications to the appropriate teams based on the type of alert.
                        </p>
                    </md-dialog-content>
                    
                    <md-dialog-actions layout="row">
                        <span flex></span>
                        <md-button ng-click="closeDialog()" class="md-primary">
                            Got it
                        </md-button>
                    </md-dialog-actions>
                </md-dialog>
            `,
            controller: ['$scope', 'DialogService', 'exampleImage', function($scope, DialogService, exampleImage) {
                $scope.closeDialog = function() {
                    DialogService.cancel();
                };
                $scope.contactGroupExampleImage = exampleImage;
            }],
            locals: {
                exampleImage: this.contactGroupExampleImage
            },
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true
        });
    }
} 